/**
 * Platform admin card: clear an email's OTP sign-in lockouts (SEC-32,
 * CLAUDE.md §3.6). Wires POST /api/admin/otp-lockout/clear.
 *
 * Its own component (not inline in SettingsView) so typing in the email field
 * doesn't remount the input on every keystroke.
 */
import React, { useState } from 'react';
import { TextInput } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminSectionCard, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import { confirmDialog } from '../../utils/dialogs';
import { normalizeLockoutEmail, otpLockoutClearErrorKey } from '../../utils/otpLockoutClear';

export default function OtpLockoutClearCard() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [emailInput, setEmailInput] = useState('');
  const [clearing, setClearing] = useState(false);

  const handleClear = async () => {
    const email = normalizeLockoutEmail(emailInput);
    if (!email) {
      showToast(t('platformAdmin.otpLockout.invalidEmail'), 'error');
      return;
    }
    const confirmed = await confirmDialog(
      t('platformAdmin.otpLockout.confirmTitle'),
      t('platformAdmin.otpLockout.confirmMessage', { email }),
      t('platformAdmin.otpLockout.action'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    setClearing(true);
    try {
      await apiRequest({ endpoint: '/admin/otp-lockout/clear', method: 'POST', data: { email } });
      showToast(t('platformAdmin.otpLockout.success', { email }), 'success');
      setEmailInput('');
    } catch (error: any) {
      showToast(t(otpLockoutClearErrorKey(error?.status)), 'error');
    } finally {
      setClearing(false);
    }
  };

  return (
    <AdminSectionCard
      icon="lock-open"
      title={t('platformAdmin.otpLockout.title')}
      subtitle={t('platformAdmin.otpLockout.description')}
    >
      <TextInput
        value={emailInput}
        onChangeText={setEmailInput}
        placeholder={t('platformAdmin.otpLockout.emailPlaceholder')}
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        accessibilityLabel={t('platformAdmin.otpLockout.emailPlaceholder')}
        testID="otp-lockout-email"
        style={{
          borderWidth: 1,
          borderColor: ADMIN_COLORS.border,
          borderRadius: 10,
          paddingHorizontal: 12,
          paddingVertical: 8,
          color: ADMIN_COLORS.text.primary,
        }}
      />
      <Button onPress={() => void handleClear()} disabled={clearing || !emailInput.trim()}>
        <Text className="text-white font-medium">
          {clearing ? t('common.loading') : t('platformAdmin.otpLockout.action')}
        </Text>
      </Button>
    </AdminSectionCard>
  );
}
